import { DateTime } from '@ksyos/date-time';
import { uniq } from 'lodash';
import type { EpisodeCategory } from '@common/enums/index';
import { LanguageOptions, ServiceType } from '@common/enums/index';
import type { AbortableFetch } from '../../../../store/fetch';
import type { IPreferredServiceOrganizationProviderWithSpecialization } from '../../../interfaces/preferredServiceOrganizationProviderWithSpecialization';
import type { IServiceConsumer } from '../../../interfaces/serviceConsumer';
import type { IServiceOrganizationConsumer } from '../../../interfaces/serviceOrganizationConsumer';
import type { IServiceOrganizationProvider } from '../../../interfaces/serviceOrganizationProvider';
import type { IServiceProvider } from '../../../interfaces/serviceProvider';
import type { ISpecialization } from '../../../interfaces/specialization';
import type { IPreferredServiceOrganizationProviderWithWaitingTime } from './PersonalSettingsPreferenceListReferralSelectProviders';
import type { IPreferredServiceOrganizationProviderWithServiceProviders } from './preferredServiceOrganizationProviderWithServiceProviders';
import { fetchJson } from '../../../../store/fetch';

export async function fetchPreferenceListServiceConsumers(
    organizationId: string,
    episodeCategory: EpisodeCategory,
    isReadOnly: boolean,
    employmentId?: string
) {
    const isActiveQueryParameter = isReadOnly ? '' : '&isOrWillBecomeActive=true';
    const isEmploymentOrOrganizationId = employmentId
        ? `employmentId=${employmentId}`
        : `organizationId=${organizationId}`;
    const [examinationConsumers, consultationConsumers, referralConsumers] = await Promise.all([
        fetchJson(
            `/api/serviceConsumers?${isEmploymentOrOrganizationId}&episodeCategory=${episodeCategory}${isActiveQueryParameter}&serviceTypes[]=${ServiceType.examination}&isRequestCreator=true`,
            'GET'
        ),
        fetchJson(
            `/api/serviceConsumers?${isEmploymentOrOrganizationId}&episodeCategory=${episodeCategory}${isActiveQueryParameter}&serviceTypes[]=${ServiceType.consultation}`,
            'GET'
        ),
        fetchJson(
            `/api/serviceConsumers?${isEmploymentOrOrganizationId}&episodeCategory=${episodeCategory}${isActiveQueryParameter}&serviceTypes[]=${ServiceType.referral}`,
            'GET'
        ),
    ]);

    const consumers: IServiceConsumer[] = Object.values(
        [
            ...examinationConsumers.json.items,
            ...consultationConsumers.json.items,
            ...referralConsumers.json.items,
        ].reduce((serviceConsumers, serviceConsumer) => {
            if (
                serviceConsumers[serviceConsumer.service.id] &&
                DateTime.from(serviceConsumers[serviceConsumer.service.id].startDate).isAfter(serviceConsumer.startDate)
            ) {
                return serviceConsumers;
            }

            serviceConsumers[serviceConsumer.service.id] = serviceConsumer;

            return serviceConsumers;
        }, {})
    );

    return consumers;
}

export async function fetchPreferenceListServiceProviders(employmentId: string, episodeCategory: EpisodeCategory) {
    const examinationProviders = await fetchJson(
        `/api/serviceProviders?employmentId=${employmentId}&episodeCategory=${episodeCategory}&isOrWillBecomeActive=true&serviceTypes[]=${ServiceType.examination}&isRequestCreator=true&isServiceConsumerSelector=true`,
        'GET'
    );

    return examinationProviders.json.items as IServiceProvider[];
}

export async function episodeCategoryHasPreferenceListServiceProviders(
    employmentId: string,
    episodeCategory: EpisodeCategory
) {
    return (await fetchPreferenceListServiceProviders(employmentId, episodeCategory)).length > 0;
}

export async function loadPreferredServiceOrganizationsWithServiceProviders(
    abortableFetch: AbortableFetch,
    organizationSettingId: string,
    serviceId: string,
    cityZipCodeOrStateSearchText?: string,
    serviceProviderNameOrSpecializationOrOrganizationSearchText?: string,
    page?: number,
    limit?: number,
    sortKey?: string,
    sortDirection?: string,
    isReadOnly?: boolean,
    needsToHaveAvailableRecipients?: boolean
) {
    const isActiveQueryParameter = isReadOnly ? '' : '&isActive=true';
    let url = `/api/preferredServiceOrganizationProviders?serviceOrganizationSettingId=${organizationSettingId}${isActiveQueryParameter}`;

    if (cityZipCodeOrStateSearchText) {
        url += `&cityZipCodeOrStateSearchText=${cityZipCodeOrStateSearchText}`;
    }

    if (serviceProviderNameOrSpecializationOrOrganizationSearchText) {
        url += `&serviceProviderNameOrSpecializationOrOrganizationSearchText=${serviceProviderNameOrSpecializationOrOrganizationSearchText}`;
    }

    if (page) {
        url += `&page=${page}`;
    }

    if (limit) {
        url += `&limit=${limit}`;
    }

    if (sortKey) {
        url += `&sortKey=${sortKey}`;
    }

    if (sortDirection) {
        url += `&sortDirection=${sortDirection}`;
    }

    if (needsToHaveAvailableRecipients) {
        url += '&needsToHaveAvailableRecipients=true';
    }

    url += '&includeServiceProviderWithSpecializations=true';

    const result = await abortableFetch.fetchJson(url, 'GET');

    const preferredOrganizations = result.json.items as IPreferredServiceOrganizationProviderWithServiceProviders[];

    for (const preferredOrganization of preferredOrganizations) {
        const serviceOrganizationsSettingUrl = `/api/serviceOrganizationSettings?organizationId=${preferredOrganization.organization.id}&serviceId=${serviceId}${isActiveQueryParameter}`;
        const serviceOrganizationSettings = (await fetchJson(serviceOrganizationsSettingUrl, 'GET')).json.items;

        if (serviceOrganizationSettings.length > 0) {
            preferredOrganization.organization.isRequestRecipient = serviceOrganizationSettings[0].isRequestRecipient;
            preferredOrganization.organization.applicationWaitingTime =
                serviceOrganizationSettings[0].applicationWaitingTime;
            preferredOrganization.organization.treatmentWaitingTime =
                serviceOrganizationSettings[0].treatmentWaitingTime;
        }
    }

    return {
        total: result.json.total,
        preferredOrganizations,
    };
}

export async function addPreferredServiceOrganizationProvidersForServiceOrganizationSetting(
    serviceOrganizationProviderOrganizationIds: string[],
    serviceOrganizationSettingId: string
) {
    for (const serviceOrganizationProviderOrganizationId of serviceOrganizationProviderOrganizationIds) {
        await fetchJson('/api/preferredServiceOrganizationProviders', 'POST', {
            serviceOrganizationSettingId: serviceOrganizationSettingId,
            organizationId: serviceOrganizationProviderOrganizationId,
        });
    }
}

export async function findServiceOrganizationProviders(
    abortableFetch: AbortableFetch,
    serviceId: string,
    serviceOrganizationSettingId: string | undefined,
    page: number,
    limit: number,
    cityZipCodeOrStateSearchText: string,
    serviceProviderNameOrSpecializationOrOrganizationSearchText: string,
    sortKey?: string,
    sortDirection?: string,
    includeServiceProviderSpecializations?: boolean,
    isBiometrician?: boolean
) {
    let url = `/api/serviceProviderOrganizations?serviceId=${serviceId}&page=${page}&limit=${limit}`;

    if (cityZipCodeOrStateSearchText) {
        url += `&cityZipCodeOrStateSearchText=${cityZipCodeOrStateSearchText}`;
    }

    if (serviceProviderNameOrSpecializationOrOrganizationSearchText) {
        url += `&serviceProviderNameOrSpecializationOrOrganizationSearchText=${serviceProviderNameOrSpecializationOrOrganizationSearchText}`;
    }

    if (serviceOrganizationSettingId) {
        url += `&excludePreferredFromServiceOrganizationSettingId=${serviceOrganizationSettingId}`;
    }

    if (sortKey) {
        url += `&sortKey=${sortKey}`;
    }

    if (sortDirection) {
        url += `&sortDirection=${sortDirection}`;
    }

    if (includeServiceProviderSpecializations) {
        url += '&includeServiceProviderSpecializations=true';
    }

    if (isBiometrician) {
        url += '&isBiometrician=true';
    }

    const result = await abortableFetch.fetchJson(url, 'GET');

    return {
        total: result.json.total as number,
        serviceOrganizationProviders: result.json.items as IServiceOrganizationProvider[],
    };
}

export async function findServiceConsumerOrganizations({
    serviceId,
    providerId,
    page,
    limit,
    cityZipCodeOrStateSearchText = '',
    serviceConsumerNameOrOrganizationSearchText = '',
    sortKey,
    sortDirection,
    organizationIds,
    consumerOrganizationIdsToExclude,
    consumerOrganizationIdsToAdd,
}: {
    cityZipCodeOrStateSearchText?: string;
    consumerOrganizationIdsToAdd?: string[];
    consumerOrganizationIdsToExclude?: string[];
    limit: number;
    organizationIds?: string[];
    page: number;
    providerId?: string;
    serviceConsumerNameOrOrganizationSearchText?: string;
    serviceId: string;
    sortDirection?: string;
    sortKey?: string;
}) {
    let url = `/api/serviceConsumerOrganizations?serviceId=${serviceId}&page=${page}&limit=${limit}`;

    if (cityZipCodeOrStateSearchText) {
        url += `&cityZipCodeOrStateSearchText=${cityZipCodeOrStateSearchText}`;
    }

    if (serviceConsumerNameOrOrganizationSearchText) {
        url += `&serviceConsumerNameOrOrganizationSearchText=${serviceConsumerNameOrOrganizationSearchText}`;
    }

    if (providerId) {
        url += `&excludePreferredFromServiceProviderId=${providerId}`;
    }

    if (sortKey) {
        url += `&sortKey=${sortKey}`;
    }

    if (sortDirection) {
        url += `&sortDirection=${sortDirection}`;
    }
    if (organizationIds) {
        url += organizationIds.map((organizationId) => `&organizationIds[]=${organizationId}`).join('');
    }

    if (consumerOrganizationIdsToExclude) {
        url += consumerOrganizationIdsToExclude
            .map((organizationIdToExclude) => `&organizationsToExclude[]=${organizationIdToExclude}`)
            .join('');
    }

    if (consumerOrganizationIdsToAdd) {
        url += consumerOrganizationIdsToAdd
            .map((organizationIdToAdd) => `&organizationsToAdd[]=${organizationIdToAdd}`)
            .join('');
    }

    const result = await fetchJson(url, 'GET');

    return {
        total: result.json.total as number,
        serviceOrganizationConsumers: result.json.items as IServiceOrganizationConsumer[],
    };
}

export async function getPreferredOrganizationSpecializations(
    organization: IPreferredServiceOrganizationProviderWithWaitingTime | IServiceOrganizationProvider,
    language: string,
    serviceId: string
) {
    const url = `/api/employmentSpecializations?organizationId=${organization.organization.id}&serviceId=${serviceId}`;
    const serviceProviderSpecializations: ISpecialization[] = (await fetchJson(url, 'GET')).json.items.map(
        (serviceProviderSpecialization: { specialization: ISpecialization }) =>
            serviceProviderSpecialization.specialization
    );

    return getUniqueLocalizedSpecializationText(serviceProviderSpecializations, language);
}

export async function getPreferredProviderSpecializations(serviceProviderId: string, language: string) {
    const serviceProviderSpecializations: ISpecialization[] = (
        await fetchJson(`/api/employmentSpecializations?serviceProviderId=${serviceProviderId}`, 'GET')
    ).json.items;

    return getUniqueLocalizedSpecializationText(serviceProviderSpecializations, language);
}

export function addUniqueLocalizedSpecializationText(
    serviceOrganizationProviderWithSpecializations:
        | IPreferredServiceOrganizationProviderWithSpecialization[]
        | IServiceOrganizationProvider[],
    language: string
) {
    for (const serviceOrganizationProviderWithSpecialization of serviceOrganizationProviderWithSpecializations) {
        for (const serviceProvider of serviceOrganizationProviderWithSpecialization.serviceProviders) {
            if (serviceProvider.specializations) {
                serviceProvider.specializationDescriptions = getUniqueLocalizedSpecializationText(
                    serviceProvider.specializations,
                    language
                );
            }
        }
    }

    return serviceOrganizationProviderWithSpecializations;
}

function getUniqueLocalizedSpecializationText(serviceProviderSpecializations: ISpecialization[], language: string) {
    const specializationTextList = serviceProviderSpecializations.map((serviceProviderSpecialization) => {
        return language === LanguageOptions.nl
            ? serviceProviderSpecialization.descriptionNL
            : serviceProviderSpecialization.descriptionEN;
    });

    return uniq(specializationTextList);
}
